export interface CategoriaDto {
  categoria: string;
  icono: string | null;
}

export interface Negocio {
  negocioId: number;
  empresaId: number;
  nombreEmpresa: string;
  nombreNegocio: string;
  fotoNegocioUrl: string | null;
  creadoPor: string;
  fechaCreacion: string;
  estaActivo: boolean;
  urlGoogleMaps: string | null;
  direccion: string | null;
  colonia: string | null;
  ciudad: string | null;
  estado: string | null;
  codigoPostal: string | null;
  categoria: string | null;
  icono: string | null;
}

export interface CreateNegocioDto {
  empresaId: number;
  nombreNegocio: string;
  fotoNegocioUrl: string | null;
  creadoPor: string;
  estaActivo: boolean;
  urlGoogleMaps: string | null;
  direccion: string | null;
  colonia: string | null;
  ciudad: string | null;
  estado: string | null;
  codigoPostal: string | null;
  categoria: string | null;
  icono: string | null;
}

export interface UpdateNegocioDto {
  empresaId: number;
  nombreNegocio: string;
  fotoNegocioUrl: string | null;
  estaActivo: boolean;
  urlGoogleMaps: string | null;
  direccion: string | null;
  colonia: string | null;
  ciudad: string | null;
  estado: string | null;
  codigoPostal: string | null;
  categoria: string | null;
  icono: string | null;
}

class NegociosService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getCategorias(): Promise<CategoriaDto[]> {
    return this.request<CategoriaDto[]>('/negocios/categorias');
  }

  async getNegociosPublicos(categoria?: string): Promise<Negocio[]> {
    const params = categoria ? `?categoria=${encodeURIComponent(categoria)}` : '';
    return this.request<Negocio[]>(`/negocios/publicos${params}`);
  }

  async getAll(): Promise<Negocio[]> {
    return this.request<Negocio[]>('/negocios');
  }

  async getById(id: number): Promise<Negocio> {
    return this.request<Negocio>(`/negocios/${id}`);
  }

  async create(data: CreateNegocioDto): Promise<Negocio> {
    return this.request<Negocio>('/negocios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdateNegocioDto): Promise<Negocio> {
    return this.request<Negocio>(`/negocios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/negocios/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadImage(id: number, file: File): Promise<{ fotoNegocioUrl: string }> {
    const url = `${this.baseUrl}/negocios/${id}/upload-imagen`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error al subir la imagen',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    return response.json();
  }
}

export const negociosService = new NegociosService();
